import { supabaseClient } from '../../services/supabase';
import { fetchCarbonIntensityData, CarbonIntensityResult } from '../../services/carbonIntensityService';
import { getBuildingsEnergyData, calculateElectricityEmissions, calculateNaturalGasEmissions } from '../../services/buildingsEnergyDataService';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { DataService } from '../../services/dataService';
import { getWasteDataForInventory } from '../../services/wasteDataService';

// Extend Window interface to include vmtChartData
declare global {
  interface Window {
    vmtChartData?: any[];
  }
}

export interface GHGInventoryResult {
  chartData: any;
  chartOptions: any;
  latestYear?: number;
  totalEmissions?: number;
  lightDutyEmissions?: number;
  heavyDutyEmissions?: number;
  naturalGasEmissions?: number;
  percentChange?: number;
}


// Helper function to get geofips for a location
async function getGeofipsForLocation(location: string): Promise<string> {
  try {
    const { data, error } = await supabaseClient
      .from('ccas')
      .select('geofips')
      .eq('geo_name', location)
      .limit(1)
      .single();

    if (error) {
      console.warn('Failed to get geofips for location:', location, error);
      return '606000'; // Default to Berkeley geofips
    }

    return data?.geofips || '606000';
  } catch (error) {
    console.warn('Error getting geofips for location:', location, error);
    return '606000'; // Default to Berkeley geofips
  }
}


// Fetch EMFAC county filtered data for Light Duty Vehicles GHG per mile
// Returns both county-specific data and California average data for fallback
async function fetchEMFACData(location: string): Promise<{ countyData: any[]; californiaData: any[] }> {
  // Resolve county for the city to query EMFAC at the county level
  await supabaseLocationService.initialize().catch(() => {});
  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');

  // Try different county name variations
  const locationVariations = Array.from(new Set([
    `${baseCountyName} County`,
    baseCountyName,
    resolvedCounty
  ]));
  
  let countyData: any[] = [];
  for (const loc of locationVariations) {
    console.log(`Trying EMFAC county filtered for location: ${loc}`);
    const { data, error } = await supabaseClient
      .from('emfac_county_filtered')
      .select('year, ghg_per_mile')
      .eq('location_name', loc)
      .eq('class', 'LDV')
      .order('year', { ascending: true });
    
    if (error) {
      console.log('EMFAC county filtered query error for', loc, ':', error);
      continue;
    }
    
    if (data && data.length > 0) {
      console.log('EMFAC county filtered data found for', loc, ':', data);
      
      // Use ghg_per_mile directly from the table
      countyData = data.map(item => ({
        year: item.year,
        ghg_per_mile: item.ghg_per_mile || 0
      }));
      
      console.log('Processed EMFAC county data (tons CO2 per mile):', countyData);
      break; // Found county data, exit loop
    }
  }
  
  // Always fetch California average data for fallback
  let californiaData: any[] = [];
  const californiaVariations = ['California', 'CA', 'California State', 'State of California'];
  
  for (const californiaName of californiaVariations) {
    console.log(`Fetching California EMFAC county filtered for fallback: ${californiaName}`);
    const { data: californiaDataResult, error: californiaError } = await supabaseClient
      .from('emfac_county_filtered')
      .select('year, ghg_per_mile')
      .eq('location_name', californiaName)
      .eq('class', 'LDV')
      .order('year', { ascending: true });
    
    if (californiaError) {
      console.log(`California EMFAC county filtered query error for ${californiaName}:`, californiaError);
      continue;
    }
    
    if (californiaDataResult && californiaDataResult.length > 0) {
      console.log(`Using California EMFAC county filtered data for ${californiaName}:`, californiaDataResult);
      
      californiaData = californiaDataResult.map(item => ({
        year: item.year,
        ghg_per_mile: item.ghg_per_mile || 0
      }));
      
      console.log('Processed California EMFAC data (tons CO2 per mile):', californiaData);
      break; // Found California data, exit loop
    }
  }
  
  if (californiaData.length === 0) {
    console.warn('⚠️ No California average EMFAC data found for fallback!');
  }
  
  return { countyData, californiaData };
}



/**
 * Fetch VMT data for GHG Inventory
 * Preferentially uses Transportation page data, falls back to Supabase if not available
 * Data is cached in global state for persistence across navigation
 */
async function fetchVMTDataForGHGInventory(location: string, geofips?: number | null): Promise<any[]> {
  console.log('🔍 GHG Inventory: Fetching VMT data for', location);
  console.log('🔍 GHG Inventory: PRIORITY - Using VMT data directly from VMT Chart (Transportation page) to ensure consistency and respect user modifications');
  
  try {
    // PRIORITY 1: Try to get data from Transportation page (legacy window variable)
    // This is set by Transportation page and includes user customizations
    if (typeof window !== 'undefined' && window.vmtChartData && window.vmtChartData.length > 0) {
      console.log('✅ GHG Inventory: Using VMT Chart data (window.vmtChartData):', window.vmtChartData.length, 'records');
      console.log('✅ GHG Inventory: This data comes from the VMT Chart on Transportation page and includes any user modifications');
      let vmtData = window.vmtChartData.map((d: any) => ({
        year: d.year,
        vmt: d.vmt || 0,
        source: 'vmt_chart',
        method_note: 'VMT data from VMT Chart (Transportation page) - includes user customizations'
      }));
      
      // Check if 2022 VMT is missing or zero, and backcast from 2023 using AADT
      const vmt2022 = vmtData.find(d => d.year === 2022);
      const vmt2023 = vmtData.find(d => d.year === 2023);
      
      if ((!vmt2022 || vmt2022.vmt === 0) && vmt2023 && vmt2023.vmt > 0 && geofips) {
        console.log('⚠️ GHG Inventory: VMT for 2022 is missing or zero, backcasting from 2023 using AADT...');
        try {
          let aadtValue: number | null = null;
          
          // Try 1: Fetch AADT data by geofips
          const { data: aadtDataArray, error: aadtError } = await supabaseClient
            .from('aadt_filtered')
            .select('year, value')
            .eq('geofips', geofips)
            .eq('year', 2022)
            .limit(1);
          
          if (!aadtError && aadtDataArray && aadtDataArray.length > 0) {
            aadtValue = aadtDataArray[0].value;
            console.log(`✅ GHG Inventory: Found AADT data for 2022 (geofips ${geofips}):`, aadtValue);
          } else {
            // Try 2: Fetch AADT data by location_name (fallback - Davis has data by location_name)
            console.log(`⚠️ GHG Inventory: No AADT data for geofips ${geofips}, trying location_name: ${location}...`);
            const { data: aadtByNameData, error: aadtByNameError } = await supabaseClient
              .from('aadt_filtered')
              .select('year, value')
              .eq('location_name', location)
              .eq('year', 2022)
              .limit(1);
            
            if (!aadtByNameError && aadtByNameData && aadtByNameData.length > 0) {
              aadtValue = aadtByNameData[0].value;
              console.log(`✅ GHG Inventory: Found AADT data for 2022 (location_name ${location}):`, aadtValue);
            } else {
              // Try 3: Look up county geofips from population table
              console.log(`⚠️ GHG Inventory: No AADT data by name, trying to find county geofips...`);
              try {
                const { data: locationData, error: locationError } = await supabaseClient
                  .from('population')
                  .select('county, geography_type')
                  .eq('geofips', geofips)
                  .limit(1);
                
                if (!locationError && locationData && locationData.length > 0) {
                  const locationInfo = locationData[0];
                  
                  // Only try county lookup if this is a city
                  if (locationInfo.geography_type === 'City' && locationInfo.county) {
                    const countyName = locationInfo.county.includes('County') 
                      ? locationInfo.county 
                      : `${locationInfo.county} County`;
                    
                    // Find county geofips
                    const { data: countyData, error: countyError } = await supabaseClient
                      .from('population')
                      .select('geofips')
                      .eq('location_name', countyName)
                      .eq('geography_type', 'County')
                      .limit(1);
                    
                    if (!countyError && countyData && countyData.length > 0) {
                      const countyGeofips = countyData[0].geofips;
                      console.log(`✅ GHG Inventory: Found county geofips ${countyGeofips} for ${countyName}, trying AADT data...`);
                      
                      // Try to get AADT data for county
                      const { data: countyAadtData, error: countyAadtError } = await supabaseClient
                        .from('aadt_filtered')
                        .select('year, value')
                        .eq('geofips', countyGeofips)
                        .eq('year', 2022)
                        .limit(1);
                      
                      if (!countyAadtError && countyAadtData && countyAadtData.length > 0) {
                        aadtValue = countyAadtData[0].value;
                        console.log(`✅ GHG Inventory: Found AADT data for 2022 (county geofips ${countyGeofips}):`, aadtValue);
                      } else {
                        console.warn('⚠️ GHG Inventory: No AADT data found for county geofips:', countyAadtError);
                      }
                    }
                  }
                }
              } catch (countyLookupError) {
                console.warn('⚠️ GHG Inventory: Error looking up county for AADT fallback:', countyLookupError);
              }
              
              if (aadtValue === null) {
                console.warn('⚠️ GHG Inventory: No AADT data found for 2022 (tried geofips, location_name, and county), cannot backcast');
              }
            }
          }
          
          if (aadtValue !== null && aadtValue > 0) {
            // Backcast: VMT_2022 = VMT_2023 × AADT_2022
            // AADT values are scaling factors: VMT_year = VMT_previous_year × AADT_year
            const backcastedVMT2022 = Math.round(vmt2023.vmt * aadtValue);
            console.log(`✅ GHG Inventory: Backcasted 2022 VMT from 2023: ${backcastedVMT2022.toLocaleString()} (2023 VMT: ${vmt2023.vmt.toLocaleString()}, AADT 2022: ${aadtValue})`);
              
            // Add or update 2022 VMT in the data
            const vmt2022Index = vmtData.findIndex(d => d.year === 2022);
            if (vmt2022Index >= 0) {
              vmtData[vmt2022Index].vmt = backcastedVMT2022;
              vmtData[vmt2022Index].source = 'vmt_chart_backcasted';
              vmtData[vmt2022Index].method_note = 'VMT data from VMT Chart, 2022 backcasted from 2023 using AADT';
            } else {
              vmtData.push({
                year: 2022,
                vmt: backcastedVMT2022,
                source: 'vmt_chart_backcasted',
                method_note: 'VMT data backcasted from 2023 using AADT'
              });
              // Sort by year
              vmtData.sort((a, b) => a.year - b.year);
            }
          }
        } catch (error) {
          console.warn('⚠️ GHG Inventory: Error backcasting 2022 VMT:', error);
        }
      }
      
      console.log('✅ GHG Inventory: VMT data sample (first 5 years):', vmtData.slice(0, 5));
      console.log('✅ GHG Inventory: VMT data summary - Years:', vmtData.map(d => d.year).sort((a, b) => a - b));
      return vmtData;
    }
    
    // PRIORITY 2: Try to get data from global VMT data (set by Transportation page)
    // Import the VMT data service to check global state
    const { getVMTDataForGHG } = await import('../../services/vmtDataService');
    const globalVmtData = await getVMTDataForGHG(location, geofips || undefined);
    
    if (globalVmtData.length > 0) {
      let vmtData = globalVmtData;
      
      // Check if 2022 VMT is missing or zero, and backcast from 2023 using AADT
      const vmt2022 = vmtData.find(d => d.year === 2022);
      const vmt2023 = vmtData.find(d => d.year === 2023);
      
      if ((!vmt2022 || vmt2022.vmt === 0) && vmt2023 && vmt2023.vmt > 0 && geofips) {
        console.log('⚠️ GHG Inventory: VMT for 2022 is missing or zero, backcasting from 2023 using AADT...');
        try {
          let aadtValue: number | null = null;
          
          // Try 1: Fetch AADT data by geofips
          const { data: aadtDataArray, error: aadtError } = await supabaseClient
            .from('aadt_filtered')
            .select('year, value')
            .eq('geofips', geofips)
            .eq('year', 2022)
            .limit(1);
          
          if (!aadtError && aadtDataArray && aadtDataArray.length > 0) {
            aadtValue = aadtDataArray[0].value;
            console.log(`✅ GHG Inventory: Found AADT data for 2022 (geofips ${geofips}):`, aadtValue);
          } else {
            // Try 2: Fetch AADT data by location_name (fallback - Davis has data by location_name)
            console.log(`⚠️ GHG Inventory: No AADT data for geofips ${geofips}, trying location_name: ${location}...`);
            const { data: aadtByNameData, error: aadtByNameError } = await supabaseClient
              .from('aadt_filtered')
              .select('year, value')
              .eq('location_name', location)
              .eq('year', 2022)
              .limit(1);
            
            if (!aadtByNameError && aadtByNameData && aadtByNameData.length > 0) {
              aadtValue = aadtByNameData[0].value;
              console.log(`✅ GHG Inventory: Found AADT data for 2022 (location_name ${location}):`, aadtValue);
            } else {
              // Try 3: Look up county geofips from population table
              console.log(`⚠️ GHG Inventory: No AADT data by name, trying to find county geofips...`);
              try {
                const { data: locationData, error: locationError } = await supabaseClient
                  .from('population')
                  .select('county, geography_type')
                  .eq('geofips', geofips)
                  .limit(1);
                
                if (!locationError && locationData && locationData.length > 0) {
                  const locationInfo = locationData[0];
                  
                  // Only try county lookup if this is a city
                  if (locationInfo.geography_type === 'City' && locationInfo.county) {
                    const countyName = locationInfo.county.includes('County') 
                      ? locationInfo.county 
                      : `${locationInfo.county} County`;
                    
                    // Find county geofips
                    const { data: countyData, error: countyError } = await supabaseClient
                      .from('population')
                      .select('geofips')
                      .eq('location_name', countyName)
                      .eq('geography_type', 'County')
                      .limit(1);
                    
                    if (!countyError && countyData && countyData.length > 0) {
                      const countyGeofips = countyData[0].geofips;
                      console.log(`✅ GHG Inventory: Found county geofips ${countyGeofips} for ${countyName}, trying AADT data...`);
                      
                      // Try to get AADT data for county
                      const { data: countyAadtData, error: countyAadtError } = await supabaseClient
                        .from('aadt_filtered')
                        .select('year, value')
                        .eq('geofips', countyGeofips)
                        .eq('year', 2022)
                        .limit(1);
                      
                      if (!countyAadtError && countyAadtData && countyAadtData.length > 0) {
                        aadtValue = countyAadtData[0].value;
                        console.log(`✅ GHG Inventory: Found AADT data for 2022 (county geofips ${countyGeofips}):`, aadtValue);
                      } else {
                        console.warn('⚠️ GHG Inventory: No AADT data found for county geofips:', countyAadtError);
                      }
                    }
                  }
                }
              } catch (countyLookupError) {
                console.warn('⚠️ GHG Inventory: Error looking up county for AADT fallback:', countyLookupError);
              }
              
              if (aadtValue === null) {
                console.warn('⚠️ GHG Inventory: No AADT data found for 2022 (tried geofips, location_name, and county), cannot backcast');
              }
            }
          }
          
          if (aadtValue !== null && aadtValue > 0) {
            // Backcast: VMT_2022 = VMT_2023 × AADT_2022
            // AADT values are scaling factors: VMT_year = VMT_previous_year × AADT_year
            const backcastedVMT2022 = Math.round(vmt2023.vmt * aadtValue);
            console.log(`✅ GHG Inventory: Backcasted 2022 VMT from 2023: ${backcastedVMT2022.toLocaleString()} (2023 VMT: ${vmt2023.vmt.toLocaleString()}, AADT 2022: ${aadtValue})`);
            
            // Add or update 2022 VMT in the data
            const vmt2022Index = vmtData.findIndex(d => d.year === 2022);
            if (vmt2022Index >= 0) {
              vmtData[vmt2022Index].vmt = backcastedVMT2022;
              vmtData[vmt2022Index].source = 'vmt_chart_backcasted';
              vmtData[vmt2022Index].method_note = 'VMT data from VMT Chart, 2022 backcasted from 2023 using AADT';
            } else {
              vmtData.push({
                year: 2022,
                vmt: backcastedVMT2022,
                source: 'vmt_chart_backcasted',
                method_note: 'VMT data backcasted from 2023 using AADT'
              });
              // Sort by year
              vmtData.sort((a, b) => a.year - b.year);
            }
          }
        } catch (error) {
          console.warn('⚠️ GHG Inventory: Error backcasting 2022 VMT:', error);
        }
      }
      
      console.log('✅ GHG Inventory: Using VMT Chart data (global state):', vmtData.length, 'records');
      console.log('✅ GHG Inventory: This data comes from the VMT Chart on Transportation page');
      console.log('✅ GHG Inventory: VMT data sample (first 5 years):', vmtData.slice(0, 5));
      console.log('✅ GHG Inventory: VMT data summary - Years:', vmtData.map(d => d.year).sort((a, b) => a - b));
      return vmtData;
    }
    
    // FALLBACK: Fetch VMT data directly from Supabase using same methods as Transportation page
    // This ensures VMT data is always available even if Transportation page hasn't been visited
    // NOTE: This is a fallback - preferred source is VMT Chart data above
    console.log('⚠️ GHG Inventory: No VMT Chart data found!');
    console.log('⚠️ GHG Inventory: Falling back to direct Supabase fetch (same methodology as VMT Chart)');
    console.log('⚠️ GHG Inventory: WARNING - This data will not include any user modifications from the VMT Chart');
    
    let vmtDbData: any[] = [];
    
    if (geofips) {
      // Use the same DataService method that the Transportation page uses
      console.log('🔍 GHG Inventory: Fetching by geofips using DataService:', geofips);
      try {
        vmtDbData = await DataService.getVMTData(undefined, ['total'], geofips || undefined);
        console.log('🔍 GHG Inventory: DataService returned:', vmtDbData.length, 'records');
      } catch (error) {
        console.warn('DataService.getVMTData failed:', error);
      }
    }
    
    // If no data found by geofips, try by location name
    if (vmtDbData.length === 0) {
      console.log('🔍 GHG Inventory: Fetching by location name using DataService:', location);
      try {
        vmtDbData = await DataService.getVMTData(location, ['total']);
        console.log('🔍 GHG Inventory: DataService by location returned:', vmtDbData.length, 'records');
      } catch (error) {
        console.warn('DataService.getVMTData by location failed:', error);
      }
    }
    
    // If still no data, try the scaling method (like Transportation page does)
    if (vmtDbData.length === 0 && geofips) {
      console.log('🔍 GHG Inventory: Trying DataService.getVMTDataWithScaling:', geofips);
      try {
        vmtDbData = await DataService.getVMTDataWithScaling(geofips || undefined);
        console.log('🔍 GHG Inventory: DataService scaling returned:', vmtDbData.length, 'records');
        
        // Aggregate VMT data by year - sum PRIVATE_AUTO and ON_DEMAND_AUTO modes (like Transportation page)
        if (vmtDbData.length > 0) {
          const aggregated: Record<number, number> = {};
          for (const record of vmtDbData) {
            // Only include PRIVATE_AUTO and ON_DEMAND_AUTO modes (light-duty vehicles)
            if (record.mode === 'PRIVATE_AUTO' || record.mode === 'ON_DEMAND_AUTO') {
              const year = record.year;
              aggregated[year] = (aggregated[year] || 0) + (record.vmt || 0);
            }
          }
          
          // Convert back to array format
          vmtDbData = Object.entries(aggregated).map(([year, vmt]) => ({
            year: parseInt(year),
            vmt: vmt,
            mode: 'total', // Mark as aggregated
            source: 'supabase_direct_scaled'
          }));
          
          console.log('🔍 GHG Inventory: Aggregated VMT data by year:', vmtDbData.length, 'years');
        }
      } catch (error) {
        console.warn('DataService.getVMTDataWithScaling failed:', error);
      }
    }
    
    const vmtData = vmtDbData.map((d: any) => ({
      year: d.year,
      vmt: d.vmt || 0,
      source: 'supabase_direct',
      method_note: 'VMT data fetched directly from Supabase (same source as Transportation page)'
    }));
    
    // Update global state with fetched data so it persists for subsequent calls
    if (vmtData.length > 0) {
      console.log('✅ GHG Inventory: Setting VMT data in global state for persistence');
      const { setVMTDataFromTransportation } = await import('../../services/vmtDataService');
      setVMTDataFromTransportation(vmtData);
      // Also set legacy window variable
      if (typeof window !== 'undefined') {
        window.vmtChartData = vmtData;
      }
    }
    
    console.log('✅ GHG Inventory: Using VMT data from Supabase:', vmtData.length, 'records');
    console.log('✅ GHG Inventory: Sample VMT data:', vmtData.slice(0, 5));
    
    return vmtData;
  } catch (error) {
    console.warn('Error fetching VMT data:', error);
  return [];
  }
}



export async function fetchGHGInventoryData(location: string): Promise<GHGInventoryResult> {
  console.log('🚀 GHG Inventory: Starting fetchGHGInventoryData for location:', location);
  
  try {
    // Get geofips for the location to improve VMT data fetching
    let geofips: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofips = await supabaseLocationService.getGeofips(location);
      console.log('🔍 Geofips for location:', geofips);
    } catch (error) {
      console.warn('Could not get geofips for location, will try location name:', error);
    }
    
    // Get VMT data using original GHG Inventory method (not affected by Policy Tool)
    console.log('🔍 GHG Inventory: About to call fetchVMTDataForGHGInventory');
    const vmtData = await fetchVMTDataForGHGInventory(location, geofips);
    console.log('🔍 VMT Data for GHG calculation:', vmtData);
    console.log('🔍 VMT Data length:', vmtData.length);
    console.log('🔍 VMT Data years:', vmtData.map(d => d.year));
    
    // Fetch city-specific HDV total_ghg from emfac_final_estimation using location_name (SUM per year)
    let hdvCityGHGDataByYear: Map<number, number> = new Map();
    
    // Try location_name first (case-insensitive, matches user's SQL query pattern)
    let hdvDataArray: any[] = [];
    const { data: hdvDataByName, error: hdvErrorByName } = await supabaseClient
      .from('emfac_final_estimation')
    .select('year, total_ghg')
      .ilike('location_name', location)
    .eq('class', 'HDV')
      .gte('year', 2015)
      .lte('year', 2023)
    .order('year', { ascending: true });
  
    if (!hdvErrorByName && hdvDataByName && hdvDataByName.length > 0) {
      hdvDataArray = hdvDataByName;
      console.log('📊 HDV Data found using location_name:', location, 'count:', hdvDataArray.length);
    } else {
      // Fallback to geofips if location_name didn't work
      if (geofips) {
        const { data: hdvDataByGeofips, error: hdvErrorByGeofips } = await supabaseClient
    .from('emfac_final_estimation')
    .select('year, total_ghg')
          .eq('geofips', geofips)
    .eq('class', 'HDV')
          .gte('year', 2015)
          .lte('year', 2023)
    .order('year', { ascending: true });
  
        if (!hdvErrorByGeofips && hdvDataByGeofips && hdvDataByGeofips.length > 0) {
          hdvDataArray = hdvDataByGeofips;
          console.log('📊 HDV Data found using geofips:', geofips, 'count:', hdvDataArray.length);
        } else {
          console.warn('📊 HDV Data not found using location_name or geofips. Errors:', hdvErrorByName, hdvErrorByGeofips);
        }
      }
    }
    
    // Sum total_ghg by year (in case there are multiple rows per year for same location)
    hdvDataArray.forEach((row: any) => {
      const year = row.year;
      const totalGhg = row.total_ghg || 0;
      const currentSum = hdvCityGHGDataByYear.get(year) || 0;
      hdvCityGHGDataByYear.set(year, currentSum + totalGhg);
    });
    
    console.log('📊 HDV City GHG Data from emfac_final_estimation (summed by year):', 
      Array.from(hdvCityGHGDataByYear.entries()).map(([year, total]) => ({ year, total_ghg: total }))
    );
    
    // Detect anomalous spikes in HDV emissions (data quality check)
    // Check if 2023 is > 2x the average of 2015-2022
    const hdvYears2015_2022 = Array.from(hdvCityGHGDataByYear.entries())
      .filter(([year]) => year >= 2015 && year <= 2022)
      .map(([, total]) => total);
    const hdv2023 = hdvCityGHGDataByYear.get(2023) || 0;
    
    if (hdvYears2015_2022.length > 0 && hdv2023 > 0) {
      const avg2015_2022 = hdvYears2015_2022.reduce((a, b) => a + b, 0) / hdvYears2015_2022.length;
      const max2015_2022 = Math.max(...hdvYears2015_2022);
      const spikeRatio = hdv2023 / avg2015_2022;
      const spikeRatioMax = hdv2023 / max2015_2022;
      
      if (spikeRatio > 2.0 || spikeRatioMax > 1.5) {
        console.warn(`⚠️ HDV DATA QUALITY WARNING for ${location}:`, {
          hdv2023,
          avg2015_2022: Math.round(avg2015_2022),
          max2015_2022: Math.round(max2015_2022),
          spikeRatio: spikeRatio.toFixed(2),
          spikeRatioMax: spikeRatioMax.toFixed(2),
          message: `HDV emissions in 2023 (${Math.round(hdv2023)} MT CO2e) are ${spikeRatio.toFixed(1)}x higher than 2015-2022 average (${Math.round(avg2015_2022)} MT CO2e). This may indicate a data quality issue in emfac_final_estimation table.`
        });
      }
    }
    
    // Fetch county-level HDV backcasting ratios for years 2005-2014
    await supabaseLocationService.initialize().catch(() => {});
    const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
    const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
    const countyVariations = Array.from(new Set([
      `${baseCountyName} County`,
      baseCountyName,
      resolvedCounty
    ]));
    
    let hdvBackcastingRatios: { year: number; backcasting_ratio: number }[] = [];
    
    // Try location_name first
    for (const locName of countyVariations) {
      const { data, error } = await supabaseClient
        .from('emfac_county_filtered')
        .select('year, backcasting_ratio')
        .eq('location_name', locName)
        .eq('class', 'HDV')
        .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
        .order('year', { ascending: true });
      if (!error && data && data.length > 0) {
        hdvBackcastingRatios = data as any;
        console.log('📊 HDV Backcasting Ratios from emfac_county_filtered (location_name) for', locName, ':', hdvBackcastingRatios.length, 'years');
        break;
      }
    }
    
    // Fallback: Try to get county geofips and query by geofips
    if (hdvBackcastingRatios.length === 0 && geofips) {
      try {
        // Get location info to find county
        const { data: locationData } = await supabaseClient
          .from('population')
          .select('county, geography_type')
          .eq('geofips', geofips)
          .limit(1);
        
        if (locationData && locationData.length > 0 && locationData[0].county) {
          const countyName = locationData[0].county;
          // Try to get county geofips (typically 4-digit codes like 6001)
          const { data: countyData } = await supabaseClient
            .from('population')
            .select('geofips')
            .eq('location_name', countyName)
            .eq('geography_type', 'County')
            .limit(1);
          
          if (countyData && countyData.length > 0) {
            const countyGeofips = countyData[0].geofips;
            console.log('📊 Trying county geofips for HDV backcasting:', countyGeofips);
            const { data, error } = await supabaseClient
              .from('emfac_county_filtered')
              .select('year, backcasting_ratio')
              .eq('geofips', countyGeofips)
              .eq('class', 'HDV')
              .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
              .order('year', { ascending: true });
            if (!error && data && data.length > 0) {
              hdvBackcastingRatios = data as any;
              console.log('📊 HDV Backcasting Ratios from emfac_county_filtered (geofips) for county geofips', countyGeofips, ':', hdvBackcastingRatios.length, 'years');
            }
          }
        }
      } catch (e) {
        console.warn('📊 Error trying to get county geofips for HDV backcasting:', e);
      }
    }
    
    if (hdvBackcastingRatios.length === 0) {
      console.warn('📊 HDV Backcasting Ratios not found for any county variation');
    }
    
    const { countyData: emfacCountyData, californiaData: emfacCaliforniaData } = await fetchEMFACData(location);

    // Calculate light duty vehicle emissions (2005-2023 only)
    const lightDutyEmissions = [];
    console.log('🔍 VMT Data for GHG calculation:', vmtData);
    console.log('🔍 VMT Data length:', vmtData.length);
    console.log('🔍 VMT Data years:', vmtData.map(d => d.year));
    console.log('📊 EMFAC county emission factor data:', emfacCountyData);
    console.log('📊 EMFAC California average emission factor data:', emfacCaliforniaData);
    
    // Log emission factor data source for troubleshooting
    console.log('📊 Light-Duty Emission Factors - Data Source: emfac_county_filtered');
    console.log('📊 Light-Duty Emission Factors - Location:', location);
    console.log('📊 Light-Duty Emission Factors - County Data Available Years:', emfacCountyData.map(d => `${d.year}: ${d.ghg_per_mile?.toFixed(6) || 'N/A'} tons CO2/mile`));
    console.log('📊 Light-Duty Emission Factors - California Average Available Years:', emfacCaliforniaData.map(d => `${d.year}: ${d.ghg_per_mile?.toFixed(6) || 'N/A'} tons CO2/mile`));
    
    // Calculate average emission factor from county data for reference
    const validCountyEmissionFactors = emfacCountyData.filter(d => d.ghg_per_mile > 0);
    const avgCountyEmissionFactor = validCountyEmissionFactors.length > 0 
      ? validCountyEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validCountyEmissionFactors.length
      : 0;
    
    // Calculate average emission factor from California data for fallback
    const validCaliforniaEmissionFactors = emfacCaliforniaData.filter(d => d.ghg_per_mile > 0);
    const avgCaliforniaEmissionFactor = validCaliforniaEmissionFactors.length > 0 
      ? validCaliforniaEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validCaliforniaEmissionFactors.length
      : 0.0008; // Final fallback emission factor (tons CO2 per mile)
    
    console.log('📊 Light-Duty Emission Factors - County Average:', avgCountyEmissionFactor > 0 ? avgCountyEmissionFactor.toFixed(6) : 'N/A', 'tons CO2/mile');
    console.log('📊 Light-Duty Emission Factors - California Average:', avgCaliforniaEmissionFactor.toFixed(6), 'tons CO2/mile');
    console.log('📊 Light-Duty Emission Factors - Final Fallback (if no data):', '0.000800 tons CO2/mile');
    
    for (let year = 2005; year <= 2023; year++) {
      const vmt = vmtData.find(d => d.year === year)?.vmt || 0;
      
      // Priority 1: Use year-specific emission factor from county data
      let yearEmissionFactorData = emfacCountyData.find(d => d.year === year);
      let emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
      let source = 'county-year-specific';
      
      // Priority 2: If county year-specific not available, use California year-specific
      if (emissionFactor === 0) {
        yearEmissionFactorData = emfacCaliforniaData.find(d => d.year === year);
        emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
        source = 'california-year-specific';
      }
      
      // Priority 3: If no year-specific data, use county average
      if (emissionFactor === 0 && avgCountyEmissionFactor > 0) {
        emissionFactor = avgCountyEmissionFactor;
        source = 'county-average';
      }
      
      // Priority 4: If no county average, use California average
      if (emissionFactor === 0) {
        emissionFactor = avgCaliforniaEmissionFactor;
        source = 'california-average';
      }
      
      // Priority 5: Final fallback
      if (emissionFactor === 0) {
        emissionFactor = 0.0008;
        source = 'final-fallback';
      }
      
      const finalEmissionFactor = emissionFactor;
      const emissions = vmt * finalEmissionFactor;
      
      // Detailed logging for troubleshooting
      console.log(`🔍 Light Duty ${year}: VMT=${vmt.toLocaleString()}, Emission Factor (${source})=${finalEmissionFactor.toFixed(6)} tons CO2/mile, Emissions=${Math.round(emissions).toLocaleString()} MT CO2e`);
      
      lightDutyEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }
    
    // Summary log of emission factors used
    console.log('📊 Light-Duty Emission Factors Summary:');
    for (let year = 2005; year <= 2023; year++) {
      let yearEmissionFactorData = emfacCountyData.find(d => d.year === year);
      let emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
      let source = '✓ county-year';
      
      if (emissionFactor === 0) {
        yearEmissionFactorData = emfacCaliforniaData.find(d => d.year === year);
        emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
        source = '○ california-year';
      }
      
      if (emissionFactor === 0 && avgCountyEmissionFactor > 0) {
        emissionFactor = avgCountyEmissionFactor;
        source = '○ county-avg';
      }
      
      if (emissionFactor === 0) {
        emissionFactor = avgCaliforniaEmissionFactor;
        source = '○ california-avg';
      }
      
      if (emissionFactor === 0) {
        emissionFactor = 0.0008;
        source = '○ fallback';
      }
      
      console.log(`  ${year}: ${emissionFactor.toFixed(6)} tons CO2/mile (${source})`);
    }

    // Validate and smooth HDV emissions data: cap year-over-year changes at 50%
    // If change > 50%, assume no change (use previous year's value)
    // First, create smoothed emissions map
    const smoothedHdvEmissionsByYear = new Map<number, number>();
    const hdvYears = Array.from(hdvCityGHGDataByYear.keys()).sort((a, b) => a - b);
    
    for (let i = 0; i < hdvYears.length; i++) {
      const year = hdvYears[i];
      const currentValue = hdvCityGHGDataByYear.get(year) || 0;
      
      if (i === 0) {
        // First year - use as-is
        smoothedHdvEmissionsByYear.set(year, currentValue);
      } else {
        const previousYear = hdvYears[i - 1];
        const previousValue = smoothedHdvEmissionsByYear.get(previousYear) || 0;
        
        if (previousValue > 0) {
          const percentChange = Math.abs((currentValue - previousValue) / previousValue);
          
          if (percentChange > 0.5) {
            // Change > 50% - use previous year's value instead
            smoothedHdvEmissionsByYear.set(year, previousValue);
            console.warn(`⚠️ GHG Inventory: HDV emissions anomaly detected for ${location} ${year}: ${currentValue.toLocaleString()} MT CO2e (${(percentChange * 100).toFixed(1)}% change). Using previous year value: ${previousValue.toLocaleString()} MT CO2e`);
          } else {
            smoothedHdvEmissionsByYear.set(year, currentValue);
          }
        } else {
          // Previous value is zero - use current value
          smoothedHdvEmissionsByYear.set(year, currentValue);
        }
      }
    }
    
    // Calculate heavy duty vehicle emissions
    // For 2015-2023: Use smoothed SUM of total_ghg from emfac_final_estimation
    // For 2005-2014: Backcast using 2015 value and county-level backcasting_ratio from emfac_county_filtered
    const heavyDutyEmissions = [];
    
    // Get 2015 HDV emissions as baseline for backcasting (use smoothed value)
    const hdv2015 = smoothedHdvEmissionsByYear.get(2015) || hdvCityGHGDataByYear.get(2015) || 0;
    console.log('📊 HDV 2015 baseline for backcasting:', hdv2015, 'MT CO2e');
    
    for (let year = 2005; year <= 2023; year++) {
      let hdvEmissions = 0;
      
      if (year >= 2015) {
        // Use smoothed data from emfac_final_estimation (use smoothed value if available, otherwise original)
        hdvEmissions = smoothedHdvEmissionsByYear.get(year) || hdvCityGHGDataByYear.get(year) || 0;
          } else {
        // Backcast from 2015 using county-level backcasting_ratio
        const backcastRatio = hdvBackcastingRatios.find(r => r.year === year);
        if (backcastRatio && backcastRatio.backcasting_ratio && hdv2015 > 0) {
          hdvEmissions = hdv2015 * backcastRatio.backcasting_ratio;
          console.log(`📊 HDV Backcast ${year}: ratio=${backcastRatio.backcasting_ratio.toFixed(4)}, 2015 baseline=${hdv2015}, backcasted=${Math.round(hdvEmissions)}`);
        } else {
          // If no ratio available, use 2015 value as fallback
          hdvEmissions = hdv2015;
          console.log(`📊 HDV ${year}: No backcasting ratio, using 2015 baseline=${hdv2015}`);
        }
      }
      
      console.log(`📊 Heavy Duty ${year}: HDV GHG (tons)=${Math.round(hdvEmissions)}`);
      
      heavyDutyEmissions.push({
        year,
        emissions: Math.round(hdvEmissions)
      });
    }

    // Calculate natural gas emissions using Buildings page data (2005-2023 only)
    // This includes user customizations and extrapolation from Buildings page
    const buildingsEnergyData = await getBuildingsEnergyData(location);
    console.log('🔍 Buildings Energy Data Debug:', {
      elecResYears: buildingsEnergyData.electricity.residential.years.length,
      elecComYears: buildingsEnergyData.electricity.commercial.years.length,
      gasResYears: buildingsEnergyData.naturalGas.residential.years.length,
      gasComYears: buildingsEnergyData.naturalGas.commercial.years.length
    });

    const naturalGasEmissions = [];
    for (let year = 2005; year <= 2023; year++) {
      const emissions = calculateNaturalGasEmissions(buildingsEnergyData, year);
      naturalGasEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    // Calculate electricity emissions using Buildings page data (2005-2023 only)
    // This includes user customizations and extrapolation from Buildings page
    const electricityEmissions = [];
    for (let year = 2005; year <= 2023; year++) {
      const emissions = calculateElectricityEmissions(buildingsEnergyData, year);
      electricityEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    // Get geofips for carbon intensity lookup
    const carbonIntensityGeofips = await getGeofipsForLocation(location);
    console.log('🔍 Carbon Intensity Debug:', { location, geofips: carbonIntensityGeofips });

    // Fetch carbon intensity data for the location
    let carbonIntensityData: CarbonIntensityResult | null = null;
    try {
      carbonIntensityData = await fetchCarbonIntensityData(carbonIntensityGeofips);
      console.log('✅ Carbon intensity data fetched:', {
        latestValue: carbonIntensityData.latestValue,
        latestYear: carbonIntensityData.latestYear,
        dataSource: carbonIntensityData.dataSource
      });
    } catch (error) {
      console.warn('⚠️ Failed to fetch carbon intensity data, using fallback:', error);
      carbonIntensityData = null;
    }

    // Use dynamic carbon intensity or fallback to 200 g CO2/kWh
    const fallbackCarbonIntensity = 0.0002; // MT CO2 per kWh (200 g CO2/kWh)
    
    // Helper function to get carbon intensity for a specific year
    const getCarbonIntensityForYear = (year: number): number => {
      if (carbonIntensityData && carbonIntensityData.chartData) {
        const yearIndex = carbonIntensityData.chartData.labels.indexOf(year);
        if (yearIndex !== -1 && carbonIntensityData.chartData.datasets[0]?.data[yearIndex] !== undefined) {
          // Convert from g CO2/kWh to MT CO2/kWh
          const intensity = carbonIntensityData.chartData.datasets[0].data[yearIndex] / 1000000;
          console.log(`🔍 Carbon intensity for ${year}: ${carbonIntensityData.chartData.datasets[0].data[yearIndex]} g CO2/kWh = ${intensity} MT CO2/kWh`);
          return intensity;
        }
      }
      console.log(`🔍 Using fallback carbon intensity for ${year}: ${fallbackCarbonIntensity} MT CO2/kWh`);
      return fallbackCarbonIntensity;
    };

    // Calculate electricity emissions using Buildings page data with dynamic carbon intensity
    const electricityCommercialEmissions = [];
    const electricityResidentialEmissions = [];
    
    for (let year = 2005; year <= 2023; year++) {
      const carbonIntensity = getCarbonIntensityForYear(year);
      
      // Calculate commercial electricity emissions
      const comKwh = buildingsEnergyData.electricity.commercial.values[buildingsEnergyData.electricity.commercial.years.indexOf(year)] || 0;
      const comEmissions = comKwh * carbonIntensity;
      electricityCommercialEmissions.push({
        year,
        emissions: Math.round(comEmissions)
      });
      
      // Calculate residential electricity emissions
      const resKwh = buildingsEnergyData.electricity.residential.values[buildingsEnergyData.electricity.residential.years.indexOf(year)] || 0;
      const resEmissions = resKwh * carbonIntensity;
      electricityResidentialEmissions.push({
        year,
        emissions: Math.round(resEmissions)
      });
    }

    // Calculate natural gas commercial emissions using Buildings page data
    const naturalGasCommercialEmissions = [];
    for (let year = 2005; year <= 2023; year++) {
      const comTherms = buildingsEnergyData.naturalGas.commercial.values[buildingsEnergyData.naturalGas.commercial.years.indexOf(year)] || 0;
      const emissions = comTherms * 0.005291; // 5291 grams per therm = 0.005291 metric tons
      naturalGasCommercialEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    console.log('🔍 Electricity Emissions Debug:', {
      elecCom2023: electricityCommercialEmissions.find(d => d.year === 2023),
      elecRes2023: electricityResidentialEmissions.find(d => d.year === 2023),
      gasCom2023: naturalGasCommercialEmissions.find(d => d.year === 2023),
      elecComTotal: electricityCommercialEmissions.reduce((sum, d) => sum + d.emissions, 0),
      elecResTotal: electricityResidentialEmissions.reduce((sum, d) => sum + d.emissions, 0),
      gasComTotal: naturalGasCommercialEmissions.reduce((sum, d) => sum + d.emissions, 0)
    });

    // Fetch waste emissions. PRIORITY: use Waste chart data (includes user edits).
    // Fallback: compute locally using same methodology.
    const wasteEmissions = [] as { year: number; category: string; emissions: number }[];
    // Try shared store first
    const sharedWaste = getWasteDataForInventory(location);
    if (sharedWaste) {
      for (let year = 2005; year <= 2023; year++) {
        const emissions = Math.round(sharedWaste.emissionsByYear[year] || 0);
        wasteEmissions.push({ year, category: 'Waste', emissions });
      }
    } else if (geofips) {
      try {
        // Get location info to determine if it's a city or county (need county geofips for landfill data)
        const { data: locationData } = await supabaseClient
          .from('population')
          .select('geography_type, county')
          .eq('geofips', geofips)
          .limit(1);

        let resolvedGeofips = geofips;
        let countyGeofips = geofips;

        // If it's a city, try to get county geofips for landfill emissions data
        if (locationData && locationData.length > 0 && locationData[0].geography_type === 'City') {
          const countyName = locationData[0].county;
          if (countyName) {
            const { data: countyData } = await supabaseClient
              .from('population')
              .select('geofips')
              .eq('location_name', countyName)
              .eq('geography_type', 'County')
              .limit(1);

            if (countyData && countyData.length > 0) {
              countyGeofips = countyData[0].geofips;
            }
          }
        }

        // Fetch waste tons from total_waste for the selected location
        const { data: wasteData, error: wasteError } = await supabaseClient
          .from('total_waste')
          .select('year, tons_waste')
          .eq('geofips', resolvedGeofips)
          .order('year', { ascending: true });

        if (wasteError) {
          console.warn('GHGInventorySupabase: Error fetching total_waste:', wasteError);
        }

        // Fetch landfill emissions data at county level to calculate emission factors
        const { data: landfillData, error: landfillError } = await supabaseClient
          .from('landfill_emissions_full')
          .select('year, waste_tons, ghg_emissions')
          .eq('geofips', countyGeofips)
          .order('year', { ascending: true });

        if (landfillError) {
          console.warn('GHGInventorySupabase: Error fetching landfill_emissions_full:', landfillError);
        }

        // Calculate county-specific emission factors by year
        // SUM(GHG Emissions) per county and year / SUM(waste_tons) per county and year
        const countyTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
        (landfillData || []).forEach((r: any) => {
          const year = r.year;
          if (!countyTotalsByYear[year]) {
            countyTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
          }
          countyTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
          countyTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
        });

        // Calculate emission factor (carbon intensity) for each year
        const emissionFactorsByYear: Record<number, number> = {};
        Object.keys(countyTotalsByYear).forEach(yearStr => {
          const year = parseInt(yearStr);
          const totals = countyTotalsByYear[year];
          if (totals.totalWaste > 0) {
            emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
          }
        });

        // Check if we have valid county emission factors, if not fetch statewide average by aggregating all California counties
        const hasValidCountyFactors = Object.keys(emissionFactorsByYear).length > 0 && 
          Object.values(emissionFactorsByYear).some(factor => factor > 0);
        
        if (!hasValidCountyFactors) {
          console.log('GHGInventorySupabase: No valid county emission factors, calculating statewide average from all California counties');
          
          // Fetch all California county data (geofips starting with 60, typically 4-digit county codes)
          // This aggregates all counties to get California-wide averages
          const { data: californiaLandfillData, error: californiaError } = await supabaseClient
            .from('landfill_emissions_full')
            .select('year, waste_tons, ghg_emissions, geofips')
            .gte('geofips', 6000)
            .lt('geofips', 7000) // California counties are typically 6000-6999
            .order('year', { ascending: true });

          if (!californiaError && californiaLandfillData && californiaLandfillData.length > 0) {
            // Calculate statewide emission factors
            const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
            californiaLandfillData.forEach((r: any) => {
              const year = r.year;
              if (!stateTotalsByYear[year]) {
                stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
              }
              stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
              stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
            });

            // Calculate statewide emission factors
            Object.keys(stateTotalsByYear).forEach(yearStr => {
              const year = parseInt(yearStr);
              const totals = stateTotalsByYear[year];
              if (totals.totalWaste > 0) {
                emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
              }
            });
            
            console.log('GHGInventorySupabase: Using statewide average emission factors');
          } else {
            console.warn('GHGInventorySupabase: Error fetching California statewide emission factors:', californiaError);
          }
        } else {
          // If we have some county factors but not all years, fill missing years with statewide average
          const allRequiredYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
          const missingYears = allRequiredYears.filter(year => !emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0);
          
          if (missingYears.length > 0) {
            console.log(`GHGInventorySupabase: Filling ${missingYears.length} missing years with statewide average`);
            
            // Fetch all California county data to calculate statewide averages
            const { data: californiaLandfillData, error: californiaError } = await supabaseClient
              .from('landfill_emissions_full')
              .select('year, waste_tons, ghg_emissions, geofips')
              .gte('geofips', 6000)
              .lt('geofips', 7000) // California counties are typically 6000-6999
              .order('year', { ascending: true });

            if (!californiaError && californiaLandfillData && californiaLandfillData.length > 0) {
              // Calculate statewide emission factors for missing years
              const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
              californiaLandfillData.forEach((r: any) => {
                const year = r.year;
                if (missingYears.includes(year)) {
                  if (!stateTotalsByYear[year]) {
                    stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
                  }
                  stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
                  stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
                }
              });

              // Fill in missing years with statewide factors
              Object.keys(stateTotalsByYear).forEach(yearStr => {
                const year = parseInt(yearStr);
                const totals = stateTotalsByYear[year];
                if (totals.totalWaste > 0 && (!emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0)) {
                  emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
                }
              });
            }
          }
        }

        // Project carbon intensity linearly for years 2020-2023 if data not available
        const yearsWithFactors = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        if (yearsWithFactors.length > 0) {
          const latestYearWithFactor = Math.max(...yearsWithFactors);
          const lastYear = latestYearWithFactor;
          const lastFactor = emissionFactorsByYear[lastYear];
          
          // If latest year is before 2023, project forward
          if (latestYearWithFactor < 2023) {
            // Find the last two years with data for linear projection
            const secondLastYear = yearsWithFactors[yearsWithFactors.length - 2];
            
            if (secondLastYear && secondLastYear < lastYear) {
              const secondLastFactor = emissionFactorsByYear[secondLastYear];
              
              // Calculate trend per year
              const yearsDiff = lastYear - secondLastYear;
              const factorDiff = lastFactor - secondLastFactor;
              const trendPerYear = factorDiff / yearsDiff;
              
              // Project for years 2020-2023 if missing
              for (let year = 2020; year <= 2023; year++) {
                if (!emissionFactorsByYear[year] || year > latestYearWithFactor) {
                  const yearsToProject = year - lastYear;
                  emissionFactorsByYear[year] = lastFactor + (trendPerYear * yearsToProject);
                }
              }
            } else {
              // If only one year of data, use that value for all missing years
              for (let year = 2020; year <= 2023; year++) {
                if (!emissionFactorsByYear[year] || year > latestYearWithFactor) {
                  emissionFactorsByYear[year] = lastFactor;
                }
              }
            }
          }
        }

        // Fetch population data for backcasting waste tons
        const { data: populationData } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('geofips', resolvedGeofips)
          .order('year', { ascending: true });

        // Build population by year
        const populationByYear: Record<number, number> = {};
        (populationData || []).forEach((r: any) => {
          populationByYear[r.year] = Number(r.population || 0);
        });

        // Aggregate waste tons by year for the selected location
        const wasteTonsByYear: Record<number, number> = {};
        (wasteData || []).forEach((r: any) => {
          const year = r.year;
          wasteTonsByYear[year] = (wasteTonsByYear[year] || 0) + Number(r.tons_waste || 0);
        });

        // Backcast waste tons to 2005 using population ratios
        const wasteYears = Object.keys(wasteTonsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        const earliestWasteYear = wasteYears.length > 0 ? Math.min(...wasteYears) : null;

        if (earliestWasteYear && earliestWasteYear > 2005 && populationByYear[earliestWasteYear] && populationByYear[earliestWasteYear] > 0) {
          const baseTons = wasteTonsByYear[earliestWasteYear];
          const basePopulation = populationByYear[earliestWasteYear];

          // Backcast years before earliest waste data year
          for (let year = 2005; year < earliestWasteYear; year++) {
            const yearPopulation = populationByYear[year] || basePopulation;
            const populationRatio = yearPopulation / basePopulation;
            
            // Backcast tons using population ratio
            wasteTonsByYear[year] = baseTons * populationRatio;
          }
        }

        // Backcast emission factors to 2005 if needed
        const factorYears = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        const earliestFactorYear = factorYears.length > 0 ? Math.min(...factorYears) : null;

        if (earliestFactorYear && earliestFactorYear > 2005) {
          const baseFactor = emissionFactorsByYear[earliestFactorYear];
          
          // Use earliest factor for all years before earliest data year
          for (let year = 2005; year < earliestFactorYear; year++) {
            emissionFactorsByYear[year] = baseFactor;
          }
        }

        // Calculate GHG emissions = waste_tons × emission_factor for each year (2005-2023)
        const calculatedEmissionsByYear: Record<number, number> = {};
        const allYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
        
        allYears.forEach(year => {
          const wasteTons = wasteTonsByYear[year] || 0;
          const emissionFactor = emissionFactorsByYear[year] || 0;
          
          if (wasteTons > 0 && emissionFactor > 0) {
            calculatedEmissionsByYear[year] = wasteTons * emissionFactor;
          } else {
            calculatedEmissionsByYear[year] = 0;
          }
        });
        
        // Create waste emissions array for all years 2005-2023
    for (let year = 2005; year <= 2023; year++) {
          const emissions = calculatedEmissionsByYear[year] || 0;
          wasteEmissions.push({ year, category: 'Waste', emissions: Math.round(emissions) });
        }
      } catch (error) {
        console.warn('⚠️ Failed to fetch waste emissions data, using zeros:', error);
        // Fill with zeros if fetch fails
        for (let year = 2005; year <= 2023; year++) {
          wasteEmissions.push({ year, category: 'Waste', emissions: 0 });
        }
      }
    } else {
      // Fill with zeros if no geofips
      for (let year = 2005; year <= 2023; year++) {
        wasteEmissions.push({ year, category: 'Waste', emissions: 0 });
      }
    }

    // Fetch Offroad emissions from offroad_emissions table using exact geofips match only
    const offroadEmissions = [];
    if (geofips) {
      try {
        // Fetch offroad emissions data for exact geofips only (no county fallback)
        const { data: offroadData, error: offroadError } = await supabaseClient
          .from('offroad_emissions')
          .select('year, emissions')
          .eq('geofips', geofips)
          .gte('year', 2005)
          .lte('year', 2023)
          .order('year', { ascending: true });

        if (offroadError) {
          console.warn('GHGInventorySupabase: Error fetching offroad_emissions:', offroadError);
        }

        // Map emissions by year (unique constraint ensures one row per year)
        const offroadEmissionsByYear: Record<number, number> = {};
        (offroadData || []).forEach((r: any) => {
          const year = r.year;
          const emissions = Number(r.emissions || 0);
          offroadEmissionsByYear[year] = emissions;
        });

        // Create offroad emissions array for all years 2005-2023
        // If no data found, values will be zero
        for (let year = 2005; year <= 2023; year++) {
          const emissions = offroadEmissionsByYear[year] || 0;
          offroadEmissions.push({ year, category: 'Offroad', emissions: Math.round(emissions) });
        }

        console.log('📊 Offroad Emissions:', {
          geofips,
          dataPoints: offroadData?.length || 0,
          yearsWithData: Object.keys(offroadEmissionsByYear).length,
          sampleYears: Object.keys(offroadEmissionsByYear).slice(0, 5),
          sampleEmissions: Object.keys(offroadEmissionsByYear).slice(0, 3).map(y => ({
            year: parseInt(y),
            emissions: offroadEmissionsByYear[parseInt(y)]
          }))
        });
      } catch (error) {
        console.warn('⚠️ Failed to fetch offroad emissions data, using zeros:', error);
        // Fill with zeros if fetch fails
        for (let year = 2005; year <= 2023; year++) {
          offroadEmissions.push({ year, category: 'Offroad', emissions: 0 });
        }
      }
    } else {
      // Fill with zeros if no geofips
      for (let year = 2005; year <= 2023; year++) {
        offroadEmissions.push({ year, category: 'Offroad', emissions: 0 });
      }
    }

    // Combine all data
    const allEmissions = [
      ...lightDutyEmissions.map(d => ({ ...d, category: 'Light Duty Vehicles' })),
      ...heavyDutyEmissions.map(d => ({ ...d, category: 'Heavy Duty Vehicles' })),
      ...naturalGasEmissions.map(d => ({ ...d, category: 'Natural Gas Residential' })),
      ...electricityCommercialEmissions.map(d => ({ ...d, category: 'Electricity Commercial' })),
      ...electricityResidentialEmissions.map(d => ({ ...d, category: 'Electricity Residential' })),
      ...naturalGasCommercialEmissions.map(d => ({ ...d, category: 'Natural Gas Commercial' })),
      ...wasteEmissions,
      ...offroadEmissions
    ];
    
    console.log('🔍 2023 Data Breakdown:', {
      lightDuty2023: allEmissions.find(d => d.category === 'Light Duty Vehicles' && d.year === 2023),
      heavyDuty2023: allEmissions.find(d => d.category === 'Heavy Duty Vehicles' && d.year === 2023),
      naturalGasRes2023: allEmissions.find(d => d.category === 'Natural Gas Residential' && d.year === 2023),
      elecCom2023: allEmissions.find(d => d.category === 'Electricity Commercial' && d.year === 2023),
      elecRes2023: allEmissions.find(d => d.category === 'Electricity Residential' && d.year === 2023),
      naturalGasCom2023: allEmissions.find(d => d.category === 'Natural Gas Commercial' && d.year === 2023),
      waste2023: allEmissions.find(d => d.category === 'Waste' && d.year === 2023),
      offroad2023: allEmissions.find(d => d.category === 'Offroad' && d.year === 2023),
      total2023: allEmissions.filter(d => d.year === 2023).reduce((sum, d) => sum + d.emissions, 0)
    });
    
    // Debug: Check VMT data
    console.log('VMT Data:', vmtData);
    console.log('VMT years with data:', vmtData.filter(d => d.vmt > 0).map(d => d.year));
    
    // Debug: Check all Heavy Duty data
    const heavyDutyEmissionsData = allEmissions.filter(d => d.category === 'Heavy Duty Vehicles');
    console.log('All Heavy Duty data:', heavyDutyEmissionsData);
    console.log('Heavy Duty years:', heavyDutyEmissionsData.map(d => d.year));
    console.log('Heavy Duty 2023 specifically:', heavyDutyEmissionsData.find(d => d.year === 2023));

    // Get unique years and categories
    const years = Array.from(new Set(allEmissions.map(d => d.year))).sort((a, b) => a - b);
    const categories = Array.from(new Set(allEmissions.map(d => d.category)));

    // Create datasets for each category
    const datasets = categories.map((category, index) => {
      // Color mapping based on category name - using shades of same color for related sources
      const getColorForCategory = (cat: string): { bg: string, border: string } => {
        switch (cat) {
          case 'Light Duty Vehicles':
            return { bg: 'rgba(59, 130, 246, 0.8)', border: 'rgba(59, 130, 246, 1)' };  // Transportation - darker blue
          case 'Heavy Duty Vehicles':
            return { bg: 'rgba(96, 165, 250, 0.8)', border: 'rgba(96, 165, 250, 1)' };  // Transportation - lighter blue
          case 'Natural Gas Residential':
            return { bg: 'rgba(245, 158, 11, 0.8)', border: 'rgba(245, 158, 11, 1)' };  // Natural Gas - darker amber
          case 'Natural Gas Commercial':
            return { bg: 'rgba(251, 191, 36, 0.8)', border: 'rgba(251, 191, 36, 1)' };  // Natural Gas - lighter amber
          case 'Electricity Residential':
            return { bg: 'rgba(16, 185, 129, 0.8)', border: 'rgba(16, 185, 129, 1)' };  // Electricity - darker emerald
          case 'Electricity Commercial':
            return { bg: 'rgba(52, 211, 153, 0.8)', border: 'rgba(52, 211, 153, 1)' };  // Electricity - lighter emerald
          case 'Waste':
            return { bg: 'rgba(236, 72, 153, 0.8)', border: 'rgba(236, 72, 153, 1)' };  // Pink for waste
          case 'Offroad':
            return { bg: 'rgba(255, 210, 0, 0.8)', border: 'rgba(255, 210, 0, 1)' };  // UC Gold for offroad
          default:
            return { bg: 'rgba(100, 116, 139, 0.8)', border: 'rgba(100, 116, 139, 1)' };
        }
      };
      
      const colors = getColorForCategory(category);
      const color = colors.bg;
      const borderColor = colors.border;

      const data = years.map(year => {
        const entry = allEmissions.find(d => d.category === category && d.year === year);
        const value = entry ? entry.emissions : 0;
        if (category === 'Heavy Duty Vehicles' && year === 2023) {
          console.log(`Chart data for Heavy Duty 2023: entry=`, entry, 'value=', value);
        }
        return value;
      });

      console.log(`Dataset for ${category}:`, {
        label: category,
        data: data.slice(-5), // Show last 5 years
        years: years.slice(-5),
        fullData: data // Show all data for debugging
      });

      return {
        label: category,
        data,
        backgroundColor: color,
        borderColor,
        borderWidth: 2
      };
    });

    const chartData = {
      labels: years,
      datasets
    };

    console.log('🔍 Chart Data Debug:', {
      yearsCount: years.length,
      years: years,
      datasetsCount: datasets.length,
      firstDatasetData: datasets[0]?.data,
      firstDatasetDataLength: datasets[0]?.data?.length,
      lastDatasetData: datasets[datasets.length - 1]?.data,
      lastDatasetDataLength: datasets[datasets.length - 1]?.data?.length
    });

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index' as const
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        title: {
          display: true,
          text: `GHG Inventory - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        tooltip: {
          enabled: true,
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              return `${label}: ${value.toLocaleString()} MT CO₂e`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Emissions (Metric Tons CO₂e)'
          },
          ticks: {
            callback: (value: any) => Number(value).toLocaleString()
          }
        },
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        }
      }
    };

    // Calculate metrics
    const latestYear = years[years.length - 1];
    const latestYearData = allEmissions.filter(d => d.year === latestYear);
    const totalEmissions = latestYearData.reduce((sum, d) => sum + d.emissions, 0);
    const lightDutyLatest = latestYearData.find(d => d.category === 'Light Duty Vehicles')?.emissions || 0;
    const heavyDutyLatest = latestYearData.find(d => d.category === 'Heavy Duty Vehicles')?.emissions || 0;
    const naturalGasLatest = latestYearData.find(d => d.category === 'Natural Gas Residential')?.emissions || 0;

    // Calculate percent change from 2005
    const emissions2005 = allEmissions.filter(d => d.year === 2005).reduce((sum, d) => sum + d.emissions, 0);
    const percentChange = emissions2005 > 0 ? ((totalEmissions - emissions2005) / emissions2005) * 100 : 0;

    console.log('🔍 GHG Inventory Data Debug:', {
      latestYear,
      totalEmissions,
      lightDutyLatest,
      heavyDutyLatest,
      naturalGasLatest,
      emissions2005,
      percentChange,
      allEmissions2023: allEmissions.filter(d => d.year === latestYear),
      allEmissions2005: allEmissions.filter(d => d.year === 2005),
      chartDataLabels: chartData.labels,
      chartDataDatasets: chartData.datasets.map(d => ({
        label: d.label,
        dataLength: d.data.length,
        lastValue: d.data[d.data.length - 1],
        sumOfData: d.data.reduce((sum: number, val: number) => sum + val, 0)
      })),
      totalFromChartData: chartData.datasets.reduce((sum: number, dataset: any) => 
        sum + (dataset.data[dataset.data.length - 1] || 0), 0
      )
    });

    return {
      chartData,
      chartOptions,
      latestYear,
      totalEmissions,
      lightDutyEmissions: lightDutyLatest,
      heavyDutyEmissions: heavyDutyLatest,
      naturalGasEmissions: naturalGasLatest,
      percentChange
    };

  } catch (error) {
    console.error('Error fetching GHG inventory data:', error);
    throw error;
  }
}
